"use client";

import { useState, useEffect, useMemo } from "react";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger
} from "../../../components/ui/tabs";
import {
  setComments,
  selectedApplicationData,
  setShowComments
} from "../../../store/ApplicationSlice";
import { ApplicationDetailsCard } from "../../../components/application/application-details-card";
import { CompanyDetailsCard } from "../../../components/application/company-details-card";
import { AgentDetailsCard } from "../../../components/application/agent-details-card";
import { DocumentsSection } from "../../../components/application/documents-section";
import { DecisionMatrixCard } from "../../../components/application/decision-matrix-card";
import { DecisionGrid } from "../../../components/application/decision-grid";
import { NotesTab } from "../../../components/application/notes-tab";
import { HistoryTab } from "../../../components/application/history-tab";
import { AIGeneratedIndicator } from "../../../components/ui/ai-generated-indicator";
import { Card } from "../../../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from "../../../components/ui/dialog";
import { Button } from "../../../components/ui/button";
import { useDispatch, useSelector } from "react-redux";
import fetchData from "../../../utils/fetchData";
import { CommentPin } from "../Comments/CommentPin";
import { getNotesByApplicationId } from "../../../api/notesService";
import { API_BASE_URL } from "../../../config/config";
import { getHistoryData } from "../../../api/historyService";
import { MessageSquarePlus, Sparkles, X } from "lucide-react";

export default function ApplicationDetailsPage () {
  const dispatch = useDispatch();
  const [isFullWidth, setIsFullWidth] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");
  const [statusList, setStatusList] = useState([]);
  const [timelineData, setTimelineData] = useState([]);
  const [showEnableNotesDialog, setShowEnableNotesDialog] = useState(false);

  const comments = useSelector(state => state.intakeData.comments);
  const attachments = useSelector(state => state.intakeData.attachments);
  const selectedIndex = useSelector(state => state.intakeData.selectedIndex);
  const appetiteData = useSelector(state => state.intakeData.appetiteData);
  const selectedApplicationId = useSelector(
    state => state.intakeData.selectedAppId
  );
  const selApplicationData = useSelector(
    state => state.intakeData.selectedApplicationData
  );
  const showComments = useSelector(state => state.intakeData.showComments);

  const accessToken = localStorage.getItem("access_token");
  const refreshToken = localStorage.getItem("refresh_token");
  const userDetails = JSON.parse(localStorage.getItem("userDetails")) || {};
  const username = userDetails.name || "user";

  useEffect(() => {
    if (selectedApplicationId && appetiteData.length > 0) {
      dispatch(
        selectedApplicationData(
          appetiteData.find(app => app.application_id === selectedApplicationId)
        )
      );
    }
  }, [selectedApplicationId, appetiteData, dispatch]);

  useEffect(() => {
    if (!selectedApplicationId) return;
    const fetchNotes = async () => {
      try {
        const response = await getNotesByApplicationId(
          selectedApplicationId,
          accessToken,
          refreshToken
        );
        const commentsWithClosedPins = response.map(comment => ({
          ...comment,
          user: {
            ...comment.user,
            color: "#1976d2"
          },
          is_open: false
        }));
        dispatch(setComments(commentsWithClosedPins));
      } catch (error) {
        console.error("Error fetching notes:", error);
      }
    };
    fetchNotes();
  }, [selectedApplicationId, accessToken, refreshToken, dispatch]);

  useEffect(() => {
    fetchData(
      `${API_BASE_URL}/insureai/ml/intake/applications/v2/application_status?application_id=${selApplicationData?.application_id}`,
      "GET"
    ).then(data => {
      setStatusList(data);
    });
  }, [selApplicationData]);

  useEffect(() => {
    const fetchData = async () => {
      const access_token = localStorage.getItem("access_token");
      const refresh_token = localStorage.getItem("refresh_token");
      const response = await getHistoryData({
        applicationId: selectedApplicationId,
        access_token,
        refresh_token
      });

      setTimelineData(response || []);
    };
    fetchData();
  }, [selectedApplicationId]);

  const handleCanvasClick = e => {
    if (e.target.closest(".comment-box")) return;

    // If showComments is disabled, show the enable notes dialog
    if (!showComments) {
      setShowEnableNotesDialog(true);
      return;
    }

    const canvas = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - canvas.left;
    const y = e.clientY - canvas.top;
    const sectionEl = e.target.closest("[data-section]");
    const sectionId = sectionEl?.getAttribute("data-section") || "unknown";
    const newComment = {
      id: crypto.randomUUID(),
      application_id: selectedApplicationId,
      x,
      y,
      page_number: 0,
      section_id: sectionId,
      tab: activeTab,
      text: "",
      user: {
        name: username,
        initials: username ? username.charAt(0).toUpperCase() : "",
        color: "#1976d2"
      },
      is_resolved: false,
      is_editing: true,
      is_open: true
    };

    dispatch(setComments([...comments, newComment]));
  };

  const handleEnableNotes = () => {
    dispatch(setShowComments(true));
    setShowEnableNotesDialog(false);
  };

  const filteredComments = useMemo(
    () =>
      Array.isArray(comments) ? comments.filter(c => c.tab === activeTab) : [],
    [comments, activeTab]
  );

  // --- FIXED LAYOUT FOR SCROLLING ---
  return (
    <div className="h-screen bg-gray-50 flex flex-col overflow-hidden">
      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Application Content */}
        <div className="flex-1 flex flex-col min-h-0">
          {selApplicationData && (
            <div className="flex-1 flex flex-col min-h-0">
              <div className="w-full mx-auto p-6 flex-1 flex flex-col min-h-0">
                {/* Toggle */}
                <ApplicationDetailsCard
                  isFullWidth={isFullWidth}
                  setIsFullWidth={setIsFullWidth}
                  selectedApplicationData={selApplicationData || {}}
                />
                {/* Main Content Tabs */}
                <Tabs
                  defaultValue="overview"
                  className="flex-1 flex flex-col overflow-hidden"
                  onValueChange={setActiveTab}
                  value={activeTab}
                >
                  <TabsList className="bg-white rounded-lg shadow-sm p-1 w-full justify-start flex-none">
                    <TabsTrigger
                      value="overview"
                      className="data-[state=active]:bg-[#FFF0ED] data-[state=active]:text-[#FF5533]"
                    >
                      Overview
                    </TabsTrigger>
                    <TabsTrigger
                      value="notes"
                      className="data-[state=active]:bg-[#FFF0ED] data-[state=active]:text-[#FF5533]"
                    >
                      Notes
                    </TabsTrigger>
                    <TabsTrigger
                      value="history"
                      className="data-[state=active]:bg-[#FFF0ED] data-[state=active]:text-[#FF5533]"
                    >
                      History
                    </TabsTrigger>
                    {/*  <TabsTrigger
                    value="decision"
                    className="data-[state=active]:bg-[#FFF0ED] data-[state=active]:text-[#FF5533]"
                  >
                    Decision Grid
                  </TabsTrigger> */}
                  </TabsList>

                  {/* Overview Tab Content */}
                  <TabsContent
                    value="overview"
                    className="flex-1 overflow-auto p-0"
                  >
                    <div
                      className="relative"
                      onDoubleClick={handleCanvasClick}
                      style={{ minHeight: 600 }}
                    >
                      {/* Comments */}
                      <div className="absolute top-0 left-0 z-9999 pointer-events-none">
                        {showComments &&
                          filteredComments?.map((comment, index) => {
                            const isDocumentComment =
                              comment.section_id?.startsWith(
                                "document-details-"
                              );
                            if (isDocumentComment) {
                              const selectedAttachment =
                                attachments?.[selectedIndex];
                              const expectedSectionId = `document-details-${selectedAttachment?.doc_id}-${selectedAttachment?.name}`;
                              if (comment.section_id === expectedSectionId) {
                                return (
                                  <div
                                    key={`${comment.application_id}-${index}`}
                                    style={{
                                      zIndex: 100,
                                      pointerEvents: "auto",
                                      position: "absolute",
                                      left: comment.x,
                                      top: comment.y
                                    }}
                                  >
                                    <CommentPin {...comment} />
                                  </div>
                                );
                              }
                              return null;
                            }

                            return (
                              <div
                                key={comment.id}
                                style={{
                                  zIndex: 100,
                                  pointerEvents: "auto",
                                  position: "absolute",
                                  left: comment.x,
                                  top: comment.y
                                }}
                              >
                                <CommentPin {...comment} />
                              </div>
                            );
                          })}
                      </div>
                      {/* Section Grid */}
                      <div className="grid grid-cols-3 gap-6">
                        <CompanyDetailsCard
                          data-section="company-details"
                          statusList={statusList}
                          selectedApplicationData={selApplicationData || {}}
                        />
                        <AgentDetailsCard
                          data-section="agent-details"
                          selectedApplicationData={selApplicationData || {}}
                        />
                      </div>
                      <DocumentsSection
                        data-section="document-details"
                        username={username}
                      />
                      <DecisionMatrixCard data-section="decision-matrix-details" />
                    </div>
                  </TabsContent>

                  {/* Notes Tab Content */}
                  <TabsContent
                    value="notes"
                    className="flex-1 overflow-auto p-0 h-0"
                  >
                    <NotesTab />
                  </TabsContent>

                  {/* History Tab Content */}
                  <TabsContent
                    value="history"
                    className="flex-1 overflow-auto p-0 h-0"
                  >
                    <HistoryTab timelineData={timelineData} />
                  </TabsContent>

                  {/* Decision Grid Tab Content */}
                  <TabsContent
                    value="decision"
                    className="flex-1 overflow-auto p-0"
                  >
                    <Card className="p-5 bg-white shadow-sm">
                      <h2 className="text-lg font-semibold mb-4">
                        Application Decision Grid
                      </h2>
                      <DecisionGrid
                        selectedApplicationData={selApplicationData}
                      />
                    </Card>
                  </TabsContent>
                </Tabs>
              </div>
            </div>
          )}
          {/* AI Generated Indicator */}
          <AIGeneratedIndicator />
        </div>
      </div>

      {/* Enable Notes Dialog */}
      <Dialog
        open={showEnableNotesDialog}
        onOpenChange={setShowEnableNotesDialog}
      >
        <DialogContent className="sm:max-w-lg bg-white border-0 shadow-2xl rounded-2xl p-0 overflow-hidden">
          {/* Header with gradient background */}
          <div className="bg-gradient-to-r from-orange-50 to-amber-50 p-6 border-b border-orange-100">
            <DialogHeader className="space-y-3">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                  <MessageSquarePlus className="w-6 h-6 text-orange-600" />
                </div>
                <div>
                  <DialogTitle className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                    Enable Notes Feature
                    <Sparkles className="w-5 h-5 text-orange-500" />
                  </DialogTitle>
                </div>
              </div>
              <DialogDescription className="text-gray-600 leading-relaxed">
                To add notes, set the Notes tab to ON. Would you like to turn it
                on?
              </DialogDescription>
            </DialogHeader>
          </div>

          {/* Content */}
          <div className="p-6">
            <DialogFooter className="flex gap-3 justify-end">
              <Button
                variant="outline"
                onClick={() => setShowEnableNotesDialog(false)}
                className="px-6 py-2 border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400 transition-colors"
              >
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>
              <Button
                onClick={handleEnableNotes}
                className="px-6 py-2 bg-orange-500 hover:bg-orange-600 text-white font-medium shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 border-0"
              >
                <MessageSquarePlus className="w-4 h-4 mr-2" />
                Enable Notes
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
